import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { objectToNullNmmDomain } from "../../../../student/utils/utils";
import { eventsBySectionsQuery, myCoursesFilter, studyGroupsFilter, messagesByStudyGroupsBoardQuery, nmmBoardsFilters } from "../../../utils/entitiesFiltersUtils";
import { selectEntitiesByFilters } from "../../../store/views/entityView";
import endPointsSections from "../../../store/models/endPointsSections";
import boardType from "../../../enums/boardType";
import { md5 } from "../../../utils/md5Utils";

export default function useMyLearningDomains() {

  const { domains, general } = useSelector((state) => state.uiData);
  const lang = useSelector(state => state.translate.lang);
  const myCurrentYearCourses = useSelector((state) => selectEntitiesByFilters(state, myCoursesFilter));
  const studyGroups = useSelector((state) => selectEntitiesByFilters(state, studyGroupsFilter));
  const extraNmmDomainsRaw = general?.extraNmmDomains;
  const myEventsQuery = useMemo(() => eventsBySectionsQuery(endPointsSections.myMatriculationEvents), []);
  const [myDomains, setMyDomains] = useState([]);
  const norm = (s) => (s ?? "").toString().trim().toLowerCase();

  useEffect(() => {
    if (!domains?.length)
      return;
    const domainTeachersMap = {};
    const domainToNmmIdMap = {};
    const nmmIdToDomainMap = {};
    studyGroups?.forEach((group) => {
      const domainKey = norm(group.domainName || group.name);
      if (group.id && domainKey)
        nmmIdToDomainMap[group.id] = domainKey;
    });
    myCurrentYearCourses?.forEach((course) => {
      if (course.nmmId) {
        const domainKey = nmmIdToDomainMap[course.nmmId];
        if (domainKey && Array.isArray(course.teachers)) {
          if (!domainTeachersMap[domainKey]) domainTeachersMap[domainKey] = [];
          domainTeachersMap[domainKey].push(...course.teachers.map((x) => ({ ...x })));
        }
      }
      const key = norm(course.domain);
      if (key && Array.isArray(course.teachers)) {
        if (!domainTeachersMap[key]) domainTeachersMap[key] = [];
        domainTeachersMap[key].push(...course.teachers.map((x) => ({ ...x })));
      }
    });

    studyGroups?.forEach((group) => {
      const key = norm(group.domainName || group.name);
      if (!key)
        return;
      if (Array.isArray(group.teachers)) {
        if (!domainTeachersMap[key]) domainTeachersMap[key] = [];
        domainTeachersMap[key].push(...group.teachers);
      }
      if (group.id) {
        if (!domainToNmmIdMap[key])
          domainToNmmIdMap[key] = [];
        domainToNmmIdMap[key].push(group.id);
      }
    });

    Object.keys(domainTeachersMap).forEach((key) => {
      const teachersArr = domainTeachersMap[key];
      const uniqueByKey = [];
      const seen = new Set();
      teachersArr.forEach((teacher) => {
        const tKey = teacher.firstName && teacher.lastName ? `${teacher.firstName} ${teacher.lastName}` : teacher.name;
        if (tKey && !seen.has(tKey)) {
          seen.add(tKey);
          uniqueByKey.push(teacher);
        }
      });
      domainTeachersMap[key] = uniqueByKey;
    });

    const studyGroupDomainNames = studyGroups?.map((sg) => sg.domainName).filter(Boolean) || [];
    const allNmmNullDomains = studyGroups?.filter((sg) => !sg.domainName).map((sg) => sg.name) || [];
    const mandatoryDomains = domains.filter((d) => d.mandatory).map((d) => d.name);
    const generalDomains = domains.filter((d) => d.name === "general").map((d) => d.name);
    const myCoursesDomains = myCurrentYearCourses?.filter((x) => x.domain)?.map((x) => x.domain) || [];
    const extraNmmDomains = extraNmmDomainsRaw?.filter((x) => myCoursesDomains.includes(x)) || [];
    const allDomainNames = [...new Set([...studyGroupDomainNames, ...extraNmmDomains, ...mandatoryDomains, ...allNmmNullDomains, ...generalDomains,]),];
    const allDomains = allDomainNames
      .map((domainNameRaw) => {
        const key = norm(domainNameRaw);
        const base = domains.find((d) => norm(d.name) === key) || objectToNullNmmDomain(domainNameRaw);
        return { ...base, teachers: domainTeachersMap[key] || [], nmmIds: domainToNmmIdMap[key] ?? [], nmmId: domainToNmmIdMap[key]?.[0] ?? null, };
      }).filter(Boolean)
      .map((d) => ({ ...d, order: d.order ?? 100 }))
      .sort((a, b) => a.order - b.order);

    setMyDomains(allDomains);
  }, [myCurrentYearCourses, domains, studyGroups, extraNmmDomainsRaw]);

  const allNmmIds = useMemo(() => myDomains.flatMap(d => d.nmmIds ?? []), [myDomains]);
  const messagesQuery = useMemo(
    () => messagesByStudyGroupsBoardQuery(lang, allNmmIds, boardType.messagesByNmm),
    [lang, allNmmIds]
  );
  const allNmmMessages = useSelector(state => selectEntitiesByFilters(state, messagesQuery));
  const nmmBoards = useSelector(state => selectEntitiesByFilters(state, nmmBoardsFilters));

  const lastAccessByBoardKey = useMemo(() =>
    Object.fromEntries(nmmBoards.map(b => [b.id, b.lastAccess])),
    [nmmBoards]
  );

  return useMemo(() =>
    myDomains.map(d => {
      const ids = d.nmmIds ?? [];
      const rawAccess = lastAccessByBoardKey[md5(`${boardType.messagesByNmm}_${d.nmmId}`).toLowerCase()];
      const lastAccess = rawAccess ? new Date(rawAccess).getTime() : 0;
      return {
        ...d,
        hasMessages: allNmmMessages?.some(m => m.nmmIds?.some(id => ids.includes(id))) ?? false,
        hasUnreadMessages: allNmmMessages?.some(m =>
          m.nmmIds?.some(id => ids.includes(id)) &&
          new Date(m.fromDate).getTime() > lastAccess
        ) ?? false,
      };
    }),
    [myDomains, allNmmMessages, lastAccessByBoardKey]
  );
}
